import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { sizePattern } from "@app/regex";
import { OPTION_PACKING_SIZES } from "@models/packing-sizes";
import { Subscription } from "rxjs";

@Component({
  selector: "fh-product-options",
  templateUrl: "./product-options.component.html",
  styleUrls: ["./product-options.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductOptionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProductOptionsComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
  ],
})
export class ProductOptionsComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  form!: FormGroup;

  @Input() price = 0;
  @Input() isRequestQuote = false;
  /**
   * The vendor's markup for a packing size (this country): choosing an
   * option's size fills its markup in with it. The markup can still be
   * changed by hand after.
   */
  /** Null: the market's markups are not here - saved empty, the backend fills it in. */
  @Input() markupForSize?: (size: string) => number | null;
  readonly packingSizes = OPTION_PACKING_SIZES;
  @Input() formArray!: FormArray;
  @Output() addOption = new EventEmitter<void>();
  @Output() removeOption = new EventEmitter<number>();

  constructor(private fb: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    // The page rebuilds its form after every Save, handing in new option
    // arrays; watching only the first one left later edits at "original", so
    // the backend skipped them (a packing size changed after a Save reverted).
    if (changes.formArray && !changes.formArray.firstChange) this.watchFormArray();
    if (changes.isRequestQuote && this.formArray) {
      this.applyRequestQuoteToOptions();
    }
  }

  applyRequestQuoteToOptions(): void {
    this.formArray?.controls.forEach((ctrl) => {
      const priceCtrl = ctrl.get("price");
      if (!priceCtrl) return;
      if (this.isRequestQuote) {
        priceCtrl.setValue(0, { emitEvent: false });
        priceCtrl.disable({ emitEvent: false });
      } else {
        priceCtrl.enable({ emitEvent: false });
      }
    });
    // Notify parent form control with updated values (including disabled price = 0)
    if (this.formArray) {
      this.onChange(this.formArray.getRawValue());
    }
  }

  ngOnInit(): void {
    if (!this.formArray) {
      console.error("formArray input is missing");
      return;
    }

    this.watchFormArray();

    this.applyRequestQuoteToOptions();
    console.log("ProductOptionsComponent initialized, watching formArray changes");
  }

  ngOnDestroy(): void {
    this.formArraySub?.unsubscribe();
  }

  private formArraySub?: Subscription;

  /** Marks an edited saved option as "edit" so it is saved, on the current array. */
  private watchFormArray(): void {
    this.formArraySub?.unsubscribe();
    if (!this.formArray) return;
    this.formArraySub = this.formArray.valueChanges.subscribe(() => {
      this.formArray.controls.forEach((control: AbstractControl) => {
        const stateControl = control.get("state");
        const currentState = stateControl?.value;

        // If user edited a field that was 'original' or 'view', mark it as 'edit'
        if (control.dirty && (currentState === "original" || currentState === "view")) {
          stateControl?.setValue("edit", { emitEvent: false });
        }
      });

      this.onChange(this.formArray.getRawValue());
      this.onTouched();
    });
  }

  get options(): FormArray {
    return this.form.get("options") as FormArray;
  }

  createOption(price: number, state = "edit"): FormGroup {
    return this.fb.group({
      addon_name: ["", [Validators.required, this.duplicateNameValidator()]],
      price: [price, [Validators.required, Validators.min(1), Validators.pattern(sizePattern)]],
      state: [state],
    });
  }

  triggerAddOption(): void {
    this.addOption.emit();
  }

  triggerRemoveOption(index: number): void {
    this.removeOption.emit(index);
  }

  getOptionControl(index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  /** A size was chosen: its markup goes into the option, which then saves. */
  onSizeChange(index: number, size: string | null): void {
    const group = this.getOptionControl(index);
    if (size && this.markupForSize) {
      group.get("destination_transhipper_markup")?.setValue(this.markupForSize(size));
    }
    // Set by code, not typed: mark it changed so it is saved as an edit.
    group.markAsDirty();
    group.updateValueAndValidity();
  }

  writeValue(value: any): void {
    if (value) {
      this.form.setControl(
        "options",
        this.fb.array(value.map((v: any) => this.fb.group({ ...v, state: "edit" })))
      );
    } else {
      this.form.reset();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onValidatorChange: any = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: any): void {
    this.onValidatorChange = fn;
  }

  validate(): ValidationErrors | null {
    return this.form.invalid ? { invalid: true } : null;
  }

  private duplicateNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value;
      const options = (control.parent?.parent as FormArray)?.controls || [];

      const hasDuplicate = options.some(
        (group: AbstractControl) =>
          group !== control.parent &&
          group.get("addon_name")?.value === name &&
          group.get("state")?.value !== "delete"
      );

      return hasDuplicate ? { duplicateName: true } : null;
    };
  }
}
